import React, { useEffect, useState } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import Editor from '@monaco-editor/react';
import AddShard from './AddShard';

const Shards = () => {
  const { user } = useSession();
  const [shards, setShards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedShard, setSelectedShard] = useState(null);
  const [editedData, setEditedData] = useState({
    shard: '',
    description: '',
    shard_key: '',
    shard_status: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addShardModal, setAddShardModal] = useState(false);

  // Fetch shards from server
  const fetchShards = async () => {
    if (!user?.access_token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(API_ENDPOINTS.GET_SHARDS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Shards loaded:', data);
      
      setShards(data);
      
    } catch (err) {
      console.error('Error loading shards:', err);
      setError('Failed to load shards. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShards();
  }, [user]);

  // Handle row click
  const handleRowClick = (shard) => {
    setSelectedShard(shard);
    setEditedData({
      shard: shard.shard || '',
      description: shard.description || '',
      shard_key: shard.shard_key || '',
      shard_status: shard.shard_status || ''
    });
    setHasChanges(false);
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Check if any field has changed
    const hasAnyChanges = 
      value !== selectedShard?.[field] ||
      editedData.shard !== selectedShard?.shard ||
      editedData.description !== selectedShard?.description ||
      editedData.shard_key !== selectedShard?.shard_key ||
      editedData.shard_status !== selectedShard?.shard_status;
    
    setHasChanges(hasAnyChanges);
  };

  // Handle save
  const handleSave = async () => {
    if (!selectedShard || !hasChanges) return;
    
    try {
      setSaving(true);
      
      const requestBody = {
        id: selectedShard.id,
        shard: editedData.shard,
        description: editedData.description,
        shard_key: editedData.shard_key,
        shard_status: editedData.shard_status
      };
      
      console.log('Saving shard:', requestBody);
      
      const response = await fetch(API_ENDPOINTS.SAVE_SHARD, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Shard saved successfully:', data);
      
      // Update the shard in the local state
      setShards(prev => prev.map(shard => 
        shard.id === selectedShard.id 
          ? { ...shard, ...editedData }
          : shard
      ));
      
      // Update selected shard
      setSelectedShard(prev => ({ ...prev, ...editedData }));
      setHasChanges(false);
      
    } catch (err) {
      console.error('Error saving shard:', err);
      alert('Failed to save shard. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Close panel
  const closePanel = () => {
    setSelectedShard(null);
    setEditedData({
      shard: '',
      description: '',
      shard_key: '',
      shard_status: ''
    });
    setHasChanges(false);
  };

  // Handle add shard modal
  const handleAddShardClose = () => {
    setAddShardModal(false);
  };

  const handleAddShardConfirm = () => {
    // Refresh the shards list from the server
    fetchShards();
  };

  // Handle delete shard
  const handleDeleteShard = async (shardId, shardKey) => {
    if (!window.confirm(`Are you sure you want to delete the shard "${shardKey}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.DELETE_SHARD, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: shardId })
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      console.log('Shard deleted successfully:', shardId);
      
      // Refresh the shards list
      fetchShards();
      
      // Close edit panel if the deleted shard was selected
      if (selectedShard?.id === shardId) {
        closePanel();
      }
      
    } catch (err) {
      console.error('Error deleting shard:', err);
      alert('Failed to delete shard. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Loading shards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-500">
          <p className="mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {/* Main Table Area */}
      <div className="p-6 h-full">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Shards</h3>
            <p className="text-gray-600">Configure prompt shards and reusable prompt components.</p>
          </div>
          <button
            onClick={() => setAddShardModal(true)}
            className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600 transition-colors"
          >
            Add Shard
          </button>
        </div>
        
        {shards && shards.length > 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shard Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt Area</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shards.map((shard) => (
                  <tr 
                    key={shard.id} 
                    onClick={() => handleRowClick(shard)}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedShard?.id === shard.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{shard.id}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">{shard.shard_key}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{shard.description}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                        {shard.promptArea}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {shard.shard_status ? (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          shard.shard_status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {shard.shard_status}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(shard.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteShard(shard.id, shard.shard_key);
                        }}
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        title="Delete shard"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p>No shards found.</p>
          </div>
        )}
      </div>

      {/* Overlay Panel */}
      {selectedShard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[90%] h-[90%] max-w-6xl flex flex-col">
            {/* Panel Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h4 className="text-xl font-semibold text-gray-800">Edit Shard</h4>
                <p className="text-sm text-gray-600">{selectedShard.shard_key}</p>
              </div>
              <div className="flex items-center gap-3">
                {hasChanges && (
                  <span className="text-orange-600 text-sm">Unsaved changes</span>
                )}
                <button
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                    hasChanges && !saving
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={closePanel}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Panel Content */}
            <div className="flex-1 flex p-6 gap-6 min-h-0">
              {/* Left Side - Form Fields */}
              <div className="w-1/3 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ID</label>
                  <input
                    type="text"
                    value={selectedShard.id}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Shard Key</label>
                  <input
                    type="text"
                    value={editedData.shard_key}
                    onChange={(e) => handleFieldChange('shard_key', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editedData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={editedData.shard_status}
                    onChange={(e) => handleFieldChange('shard_status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Status</option>
                    <option value="Active">Active</option>
                    <option value="Draft">Draft</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prompt Area</label>
                  <input
                    type="text"
                    value={selectedShard.promptArea}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Created</label>
                  <input
                    type="text"
                    value={new Date(selectedShard.created_at).toLocaleString()}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              {/* Right Side - Shard Editor */}
              <div className="flex-1 flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-2">Shard Content</label>
                <div className="flex-1 border border-gray-300 rounded-md overflow-hidden">
                  <Editor
                    height="100%"
                    defaultLanguage="markdown"
                    value={editedData.shard}
                    onChange={(value) => handleFieldChange('shard', value || '')}
                    options={{
                      readOnly: false,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      fontSize: 14,
                      lineNumbers: 'off',
                      folding: true,
                      automaticLayout: true
                    }}
                    theme="vs"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Shard Modal */}
      <AddShard
        isOpen={addShardModal}
        onClose={handleAddShardClose}
        onConfirm={handleAddShardConfirm}
      />
    </div>
  );
};

export default Shards;
